import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";

import { compareKeys } from "../../forms/tools";
import {
  GetFileDownloadUrlCommandInput,
  ListFilesCommandInput,
  ClaimFile,
} from "@amzn/ttechclaimintakeservice-client";
import { listFilesService } from "src/api/files/listFiles";
import { getFileService } from "src/api/files/getFileDownloadUrl";
import { deleteFileService } from "src/api/files/deleteFile";

import {
  AUTHORIZATION_FORM_DOCUMENT_TYPE,
  WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE,
  MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE,
  MEDICAL_PANEL_DOCUMENT_TYPE,
  STATE_FORM_DOCUMENT_TYPE,
} from "src/views/workersCompensationDocuments/constants";
import { uploadFileService } from "src/api/files/uploadFile";

interface DocumentTypeState {
  [index: string]: ClaimFile[] | string | boolean | File[];
  files: ClaimFile[];
  fileUrl: string;
  filesRetrieved: boolean;
  gettingFiles: boolean;
  fileUrlRetrieved: boolean;
  gettingFileUrl: boolean;
  deletingFile: boolean;
  deleteFileSuccess: boolean;
  uploadingFiles: boolean;
  uploadFilesSuccess: boolean;
  listFilesError: string;
  getFileUrlError: string;
  deleteFileError: string;
  uploadFilesError: string;
}
const initialDocumentTypeState: DocumentTypeState = {
  files: [],
  fileUrl: "",
  filesRetrieved: false,
  gettingFiles: false,
  fileUrlRetrieved: false,
  gettingFileUrl: false,
  deletingFile: false,
  deleteFileSuccess: false,
  listFilesError: "",
  getFileUrlError: "",
  deleteFileError: "",
  uploadingFiles: false,
  uploadFilesSuccess: false,
  uploadFilesError: "",
};
// Define a type for the slice state
export interface WorkersCompensationDocumentsState {
  [index: string]: DocumentTypeState;
  [MEDICAL_PANEL_DOCUMENT_TYPE]: DocumentTypeState;
  [AUTHORIZATION_FORM_DOCUMENT_TYPE]: DocumentTypeState;
  [STATE_FORM_DOCUMENT_TYPE]: DocumentTypeState;
  [WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE]: DocumentTypeState;
  [MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE]: DocumentTypeState;
}
export const initialState: WorkersCompensationDocumentsState = {
  [MEDICAL_PANEL_DOCUMENT_TYPE]: initialDocumentTypeState,
  [AUTHORIZATION_FORM_DOCUMENT_TYPE]: initialDocumentTypeState,
  [STATE_FORM_DOCUMENT_TYPE]: initialDocumentTypeState,
  [WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE]: initialDocumentTypeState,
  [MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE]: initialDocumentTypeState,
};

export const verifyStateIsValid = (state: WorkersCompensationDocumentsState) => {
  return compareKeys(state, initialState);
};
export const listFiles = createAsyncThunk(
  "workersCompensationDocuments/listFiles",
  async (input: ListFilesCommandInput) => {
    const response = await listFilesService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const getFile = createAsyncThunk(
  "workersCompensationDocuments/getFile",
  async (input: GetFileDownloadUrlCommandInput) => {
    const response = await getFileService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const deleteFile = createAsyncThunk(
  "workersCompensationDocuments/deleteFile",
  async (input: GetFileDownloadUrlCommandInput) => {
    const response = await deleteFileService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const uploadMultipleFiles = createAsyncThunk(
  "workersCompensationDocuments/uploadMultipleFiles",
  async ({ files, fileType }: { files: File[]; fileType: string }, { rejectWithValue }) => {
    const responses = [];
    for (const file of files) {
      try {
        const response = await uploadFileService(
          {
            key: file.name,
            contentLength: file.size,
            fileType,
          },
          file
        );
        responses.push(response);
      } catch (error) {
        return rejectWithValue({ error, fileType });
      }
    }
    return { responses, fileType };
  }
);

export const workersCompensationDocumentsSlice = createSlice({
  name: "workersCompensationDocuments",
  initialState,
  reducers: {
    resetWorkersCompensationDocumentsState: () => {
      return initialState;
    },
    setNewWorkersCompensationDocumentsState: (
      state: WorkersCompensationDocumentsState,
      action: PayloadAction<{
        newState: DocumentTypeState;
        fileType: string;
      }>
    ) => {
      const { newState, fileType } = action.payload;

      return { ...state, [fileType]: newState };
    },
    setValueInWorkersCompensationDocumentsState: (
      state: WorkersCompensationDocumentsState,
      action: PayloadAction<{
        key: string;
        value: string | boolean | File[];
        fileType: string;
      }>
    ) => {
      const { fileType, key, value } = action.payload;
      state[fileType][key] = value;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<WorkersCompensationDocumentsState>) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(listFiles.fulfilled, (state: WorkersCompensationDocumentsState, action) => {
        const { files, fileType } = action.payload;
        if (!fileType) return;
        state[fileType].files = files ?? [];
        state[fileType].gettingFiles = false;
        state[fileType].filesRetrieved = true;
      })
      .addCase(listFiles.pending, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;
        state[fileType].gettingFiles = true;
        state[fileType].listFilesError = "";
      })
      .addCase(listFiles.rejected, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;
        state[fileType].gettingFiles = false;
        state[fileType].filesRetrieved = false;
        state[fileType].listFilesError = action.error.message ?? "";
      })

      .addCase(getFile.fulfilled, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType, downloadUrl } = action.payload;
        if (!fileType) return;
        state[fileType].fileUrl = downloadUrl ?? "";
        state[fileType].fileUrlRetrieved = true;
        state[fileType].gettingFileUrl = false;
        state[fileType].getFileUrlError = "";
      })
      .addCase(getFile.pending, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;
        state[fileType].gettingFileUrl = true;
        state[fileType].getFileUrlError = "";
      })
      .addCase(getFile.rejected, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;

        state[fileType].fileUrlRetrieved = false;
        state[fileType].gettingFileUrl = false;
        state[fileType].getFileUrlError = action.error.message ?? "";
      })

      .addCase(deleteFile.fulfilled, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.payload;
        if (!fileType) return;
        state[fileType].deleteFileSuccess = true;
        state[fileType].deletingFile = false;
        state[fileType].deleteFileError = "";
      })
      .addCase(deleteFile.pending, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;
        state[fileType].deleteFileSuccess = false;
        state[fileType].deletingFile = true;
        state[fileType].deleteFileError = "";
      })
      .addCase(deleteFile.rejected, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;

        state[fileType].deleteFileSuccess = false;
        state[fileType].deletingFile = false;
        state[fileType].deleteFileError = action.error.message ?? "";
      })
      .addCase(
        uploadMultipleFiles.fulfilled,
        (state: WorkersCompensationDocumentsState, action) => {
          const { fileType } = action.payload;
          if (!fileType) return;
          state[fileType].uploadFilesSuccess = true;
          state[fileType].uploadingFiles = false;
          state[fileType].uploadFilesError = "";
        }
      )
      .addCase(uploadMultipleFiles.pending, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;
        state[fileType].uploadFilesSuccess = false;
        state[fileType].uploadingFiles = true;
        state[fileType].uploadFilesError = "";
      })
      .addCase(uploadMultipleFiles.rejected, (state: WorkersCompensationDocumentsState, action) => {
        const { fileType } = action.meta.arg;
        if (!fileType) return;

        state[fileType].uploadFilesSuccess = false;
        state[fileType].uploadingFiles = false;

        // rejectWithValue puts the value in action.payload
        if (action.payload) {
          const { error } = action.payload as { error: unknown; fileType: string };
          state[fileType].uploadFilesError = (error as Error)?.message ?? "Upload failed";
        } else {
          state[fileType].uploadFilesError = action.error.message ?? "";
        }
      });
  },
});

export const {
  resetWorkersCompensationDocumentsState,
  setValueInWorkersCompensationDocumentsState,
  setNewWorkersCompensationDocumentsState,
} = workersCompensationDocumentsSlice.actions;

export default workersCompensationDocumentsSlice.reducer;
